import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { notification } from 'antd';

import LogSyncContext from '@totem/components/administration/logSync/LogSyncContext';
import {
  LogSyncFilterOptions,
  LogSyncListItem,
  LogSyncReport,
} from '@totem/components/administration/logSync/types';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Params } from '@totem/types/common';
import {
  LOG_SYNC_ENDPOINT,
  LOG_SYNC_FILTERS_ENDPOINT,
} from '@totem/utilities/endpoints';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

const toFilterOptions = (values?: string[]) =>
  [...(values ?? [])]
    .sort(sortStringAscending)
    .map((value) => ({ text: value, value }));

type Props = {
  children: ReactNode;
};

const LogSyncContainer = ({ children }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const {
    buildParameters,
    setFilterOptions,
    filterUpdateCount,
    sortingUpdateCount,
    pagingUpdateCount,
    isReady,
  } = useContext(InputContext);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<LogSyncListItem[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [refreshData, setRefreshData] = useState(true);

  useEffect(() => {
    fetch(LOG_SYNC_FILTERS_ENDPOINT, {
      method: 'GET',
      headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
    })
      .then((res) => res.json())
      .then((options: LogSyncFilterOptions) => {
        setFilterOptions('name', toFilterOptions(options?.names));

        setFilterOptions('connection', toFilterOptions(options?.connections));

        setFilterOptions('site', toFilterOptions(options?.sites));
      })
      .catch(() => {
        notification.error({
          message: 'Failed to load log sync filter options.',
        });
      });
  }, [getApiToken]);

  useEffect(() => {
    if (isReady) {
      setRefreshData(true);
    }
  }, [isReady, filterUpdateCount, sortingUpdateCount, pagingUpdateCount]);

  useEffect(() => {
    if (!isReady || !refreshData) {
      return;
    }

    setLoading(true);

    const params: Params = buildParameters();

    fetch(LOG_SYNC_ENDPOINT, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(params),
    })
      .then((res) => res.json())
      .then((result: LogSyncReport) => {
        setData(result?.results ?? []);

        setTotalRecords(result?.paging?.totalRecords ?? 0);
      })
      .catch(() => {
        notification.error({ message: 'Failed to load log sync results.' });
      })
      .finally(() => setLoading(false));

    setRefreshData(false);
  }, [refreshData, isReady, getApiToken]);

  return (
    <LogSyncContext.Provider
      value={{
        loading,
        data,
        totalRecords,
        onRefresh: () => setRefreshData(true),
      }}
    >
      {children}
    </LogSyncContext.Provider>
  );
};

export default LogSyncContainer;
